import React, { useContext, useReducer, useCallback } from 'react';

type Action =
  | { type: 'SET_PAGE_LOADING'; payload: boolean }
  | { type: 'ON_ELEMENT_LOADED'; payload: string }
  | { type: 'REGISTER_ELEMENTS'; payload: string[] };

export type UseLoading = [UseLoadingState, UseLoadingDispatch];
export interface UseLoadingState {
  elements: Set<string>;
  elementIds: Set<string>;
  registered: boolean;
  hasPageLoaded: boolean;
}
export interface UseLoadingDispatch {
  setLoading: (loading: boolean) => void;
  onElementLoaded: (id: string) => void;
  registerElements: (elementIds: string[]) => void;
}

export const getDefaultLoadingState = (): UseLoadingState => ({
  elements: new Set(),
  elementIds: new Set(),
  registered: false,
  hasPageLoaded: false
});

const loadingReducer = (state: UseLoadingState, action: Action): UseLoadingState => {
  switch (action.type) {
    case 'SET_PAGE_LOADING':
      return {
        ...state,
        hasPageLoaded: !action.payload
      };
    case 'ON_ELEMENT_LOADED': {
      const elements = state.elements.add(action.payload);
      const allElementsLoaded = elements.size === state.elementIds.size;

      return {
        ...state,
        elements,
        hasPageLoaded: allElementsLoaded
      };
    }
    case 'REGISTER_ELEMENTS': {
      return {
        ...state,
        elementIds: new Set(action.payload),
        registered: true
      };
    }
    default:
      return getDefaultLoadingState();
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const LoadingContext = React.createContext<any>([]);

const PageContentLoadingProvider: React.FC = props => {
  const [state, dispatch] = useReducer<React.Reducer<UseLoadingState, Action>>(
    loadingReducer,
    getDefaultLoadingState()
  );

  // short-circuit
  const setLoading = useCallback(
    (loading: boolean): void => {
      dispatch({ type: 'SET_PAGE_LOADING', payload: loading });
    },
    [dispatch]
  );

  const onElementLoaded = useCallback(
    (id: string): void => {
      dispatch({ type: 'ON_ELEMENT_LOADED', payload: id });
    },
    [dispatch]
  );

  const registerElements = useCallback(
    (elementIds: string[]): void => {
      dispatch({ type: 'REGISTER_ELEMENTS', payload: elementIds });
    },
    [dispatch]
  );

  return (
    <LoadingContext.Provider value={[state, { setLoading, onElementLoaded, registerElements }]}>
      {props.children}
    </LoadingContext.Provider>
  );
};

export default PageContentLoadingProvider;

export const usePageContentLoading = (): UseLoading => {
  const [state, dispatch] = useContext(LoadingContext);

  return [state, dispatch];
};
